import React from 'react';
import { Globe, Monitor, Smartphone, Code, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const MultiPlatformSupport: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <Globe className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                Multi-platform Support
              </h1>
              <p className="text-xl text-secondary-600">
                Access your business dashboard from anywhere with responsive web and mobile apps
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                Work From Any Device
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our Multi-platform Support ensures you can access your business dashboard from 
                anywhere, on any device. Whether you prefer a desktop computer, tablet, or 
                smartphone, our responsive platform adapts to your needs.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                With web dashboards, mobile apps, API integrations, and third-party connections, 
                we provide flexible solutions that work with your existing tools and workflows.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <Globe size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Universal Access</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                Access your account from any device, anywhere in the world. Your data syncs 
                seamlessly across all platforms in real-time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-secondary-600">
              Comprehensive platform support for all your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Monitor size={40} />,
                title: 'Web Dashboard',
                description: 'Full-featured web dashboard accessible from any browser. Responsive design works on desktop, tablet, and mobile browsers.'
              },
              {
                icon: <Smartphone size={40} />,
                title: 'Mobile Apps',
                description: 'Native iOS and Android apps with full functionality. Access all features from your mobile device.'
              },
              {
                icon: <Code size={40} />,
                title: 'API Integration',
                description: 'RESTful API for custom integrations. Connect Rita with your existing systems and workflows.'
              },
              {
                icon: <Globe size={40} />,
                title: 'Third-party Integrations',
                description: 'Integrate with popular tools and services. Connect with accounting software, CRM systems, and more.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Real-time Sync',
                description: 'All data syncs in real-time across platforms. Changes on one device appear instantly on all others.'
              },
              {
                icon: <Monitor size={40} />,
                title: 'Responsive Design',
                description: 'Optimized for all screen sizes. Works perfectly on desktop, tablet, and mobile devices.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why Multi-platform Support?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits that provide flexibility and convenience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Work From Anywhere',
                description: 'Access your business dashboard from any device, anywhere. Work from office, home, or on the go.'
              },
              {
                title: 'Device Flexibility',
                description: 'Use the device that\'s most convenient for you. Switch between desktop, tablet, and mobile seamlessly.'
              },
              {
                title: 'Custom Integrations',
                description: 'Connect Rita with your existing tools and systems. API access enables custom integrations.'
              },
              {
                title: 'Consistent Experience',
                description: 'Same features and functionality across all platforms. Learn once, use everywhere.'
              },
              {
                title: 'No Vendor Lock-in',
                description: 'Open API and integration options mean you\'re not locked into a single platform or vendor.'
              },
              {
                title: 'Future-Proof',
                description: 'As new devices and platforms emerge, we ensure compatibility. Your investment is protected.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Access Rita from any device and start managing your business today
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Contact Sales
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MultiPlatformSupport;

